import { v4 as uuidV4 } from 'uuid';
import { USER_SESSIONS_LOCAL_REF } from '../../components/_core/OfflineHandler/offline-handler.container';
import { IUserActionAck, IUserSessionStore, UserActionType } from '../types';
import { store } from './storage';
import { actionKey } from '../helpers/user-action-ack.helpers';
import { IRecordCompositeScoreInput, IRecordScoreInput, IScoreActionRefs } from '../../components/Score/ManageEvaluations/manage-evaluations.interface';
import { toEpochMillis } from '../helpers/date.helpers';
import { actionBufferTimeoutMs, withTimeout } from '../helpers/with-timeout.helpers';
import _ from 'lodash';

// Raised when a score never reached device storage, so the caller tells the scorer instead of
// leaving them believing it saved. `reason` separates a signed-out tap from a failed write.
export class ScoreNotBufferedError extends Error {
    readonly reason: 'no-user' | 'write-failed'

    constructor(reason: 'no-user' | 'write-failed', cause?: unknown) {
        super(`Score was not buffered on this device (${reason})`)
        this.name = 'ScoreNotBufferedError'
        this.reason = reason
        if (cause !== undefined) (this as { cause?: unknown }).cause = cause
    }
}

// Raised when a read outside the write queue doesn't settle in time. Nothing was lost — the buffer
// just couldn't be read — so it is distinct from ScoreNotBufferedError.
export class BufferReadTimeoutError extends Error {
    readonly reason = 'read-timeout'

    constructor(readonly timeoutMs: number) {
        super(`Buffered actions could not be read within ${timeoutMs}ms`)
        this.name = 'BufferReadTimeoutError'
    }
}

// Every read-modify-write of the action buffer runs one at a time in this tab, so a score buffered while a
// sync prunes is never overwritten by the prune's stale copy. Network calls never run inside the lock.
let bufferQueue: Promise<unknown> = Promise.resolve()

// The only way a queued operation stores the buffer. See CLAUDE.md "Two storage tiers" (timed-out writes).
type CommitUserSessions = (userSessions: IUserSessionStore['userSessions']) => Promise<void>

const withinTab = <T>(write: (commit: CommitUserSessions) => Promise<T>): Promise<T> => {
    const timeoutMs = actionBufferTimeoutMs()
    const guardedWrite = () => {
        let abandoned = false
        let storing = false
        // A write the queue gave up on holds a stale read; storing it would erase the writes that ran since.
        const commit: CommitUserSessions = (userSessions) => {
            if (!abandoned) {
                storing = true
                return writeUserSessions(userSessions)
            }
            console.error('user-session action-buffer write: a timed-out write finished late; discarded it')
            return Promise.reject(new ScoreNotBufferedError('write-failed'))
        }
        const attempt = write(commit)
        // A write already being stored can't be cancelled, so the queue waits one more bound for its real outcome.
        return withTimeout(attempt, timeoutMs, () => {
            abandoned = true
            console.error(`user-session action-buffer write: ${storing ? 'still storing' : 'timed out'} after ${timeoutMs}ms`)
            return new ScoreNotBufferedError('write-failed')
        }).catch((err) => {
            if (!storing || !abandoned) throw err
            return withTimeout(attempt, timeoutMs, () => {
                console.error(`user-session action-buffer write: never finished storing within ${2 * timeoutMs}ms; gave up on it`)
                return new ScoreNotBufferedError('write-failed')
            })
        })
    }
    const run = bufferQueue.then(guardedWrite, guardedWrite)
    bufferQueue = run.then(() => undefined, () => undefined)
    return run
}

// Matches the offline hydrator's chunk size, so both paths bound a request the same way.
export const SUBMIT_CHUNK_SIZE = 100

export const BUFFER_LOCK_NAME = 'max_UserSessions'
// Typed optional because jsdom and pre-15.4 Safari ship no Web Locks API, though lib.dom
// declares it non-optional.
const lockManager = (): LockManager | undefined =>
    (typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { locks?: LockManager }).locks)

// Tabs share one IndexedDB store and every write replaces the whole blob, so an in-tab queue
// alone lets a second tab's stale copy erase scores. Web Locks serializes across tabs; without
// it (Safari < 15.4) the in-tab queue is still the floor. No caller holds the lock re-entrantly.
const withBufferLock = <T>(write: (commit: CommitUserSessions) => Promise<T>): Promise<T> => {
    const locks = lockManager()
    return locks ? locks.request(BUFFER_LOCK_NAME, () => withinTab(write)) : withinTab(write)
}

const writeUserSessions = (userSessions: IUserSessionStore['userSessions']) =>
    store.set(USER_SESSIONS_LOCAL_REF, JSON.stringify({userSessions}))

// Starts a fresh session. Never rejects: a failed seed is logged and the first buffered action
// creates the session, so a slow write can't block start-up or sign-in.
export const recordUserSession = (user: IUser): Promise<void> => withBufferLock(async (commit) => {
    const userSessions = await readUserSessions()

    if (user) {
        const newSession: IUserSession = {
            startedAt: new Date(),
            actions: []
        }

        // Filter out any old sessions for current user that don't have any logged actions
        const newUserSessions: IUserSession[] = []
        if (user._id in userSessions) newUserSessions.push(...userSessions[user._id].filter((curr) => !!curr.actions?.length))
        newUserSessions.push(newSession)
        userSessions[user._id] = newUserSessions

        await commit(userSessions)
    }
}).catch((error) => {
    console.error('user-session buffer: could not start a new session; the next buffered action will create one', error)
})

// Resolving means the actions are on disk. A missing user rejects rather than returning: the
// caller renders the score as saved, so a silent no-op loses it. See ScoreNotBufferedError.
export const recordUserSessionActions = (user: IUser, actions: IUserAction[]) => withBufferLock(async (commit) => {
    if (!user?._id) throw new ScoreNotBufferedError('no-user')

    const userSessions = await readUserSessions()

    // Lazy-seed: login/apollo-bootstrap normally creates this bucket; an already
    // logged-in parent can land here with none, so initialize and proceed.
    if (!userSessions[user._id]?.length) {
        userSessions[user._id] = [{ startedAt: new Date(), actions: [] }]
    }

    const currUserSessions = userSessions[user._id]
    currUserSessions[currUserSessions.length - 1].actions?.push(...actions)
    userSessions[user._id] = currUserSessions

    await commit(userSessions)
})

// Removes the acknowledged actions (by actionKey) from the stored session and returns its REMAINING actions,
// read under the lock so anything buffered during the request survives. Null when the session is gone.
export const removeUserSessionActions = (userId: string, startedAt: Date | string, keys: Iterable<string>): Promise<IUserAction[] | null> => withBufferLock(async (commit) => {
    const acked = new Set(keys)
    const userSessions = await readUserSessions()
    const session = (userSessions[userId] || []).find((s) => toEpochMillis(s.startedAt) === toEpochMillis(startedAt))
    if (!session) return null
    if (!acked.size) return session.actions

    session.actions = session.actions.filter((action) => !acked.has(actionKey(action) as string))
    await commit(userSessions)
    return session.actions
})

// Unbounded on purpose: only the write queue calls it, and the queue's own timeout bounds the whole write.
const readUserSessions = async () => {
    const { userSessions } = JSON.parse((await store.get(USER_SESSIONS_LOCAL_REF)) || '{"userSessions":{}}') as IUserSessionStore
    return userSessions
}

// Every read outside the write queue is bounded too: a wedged store hangs a read as surely as a write.
export const getCachedUserSessions = () => {
    const timeoutMs = actionBufferTimeoutMs()
    return withTimeout(readUserSessions(), timeoutMs, () => {
        console.error(`user-session action-buffer read: timed out after ${timeoutMs}ms`)
        return new BufferReadTimeoutError(timeoutMs)
    })
}

// Get the newest user session for curr user
export const getCurrUserSession = async (user: IUser) => {
    const userSessions = await getCachedUserSessions()

    if (user?._id && user._id in userSessions) {
        const currUserSessions = userSessions[user._id]
        return currUserSessions[currUserSessions.length - 1]
    }
}

// Get all sessions for user, sorted by oldest first
export const getAllCurrUsersSessions = async (user: IUser) => { 
    const userSessions = await getCachedUserSessions()

    if (user._id in userSessions) {
        const currUserSessions = userSessions[user._id]
        return currUserSessions.sort((a, b) => toEpochMillis(a.startedAt) - toEpochMillis(b.startedAt))
    }
}

// Removes the named sessions only while they are still empty in the store, so an action buffered into one
// since the caller last read it is never dropped with it.
export const filterUserSessions = (userId: string, sessionsToRemove: IUserSession[]) => withBufferLock(async (commit) => {
    if (sessionsToRemove.length) {
        const userSessions = await readUserSessions()
        if (userId in userSessions) {
            userSessions[userId] = userSessions[userId].filter((s) => s.actions?.length || !sessionsToRemove.find(r => toEpochMillis(r.startedAt) === toEpochMillis(s.startedAt)))
            if (!userSessions[userId].length) delete userSessions[userId]
            await commit(userSessions)
        }
    }
})

export const transformScoreOperationToAction = ({remove, loggedAt, ...score}: Partial<IRecordScoreInput>) => {
    return {actionType: remove ? UserActionType.removeScore : UserActionType.score, payload: {score}, loggedAt} as IUserAction
}

export const transformCompScoreOperationToAction = ({remove, loggedAt, ...score}: Partial<IRecordCompositeScoreInput>) => {
    return {actionType: remove ? UserActionType.removeCompositeScore : UserActionType.compositeScore, payload: {score}, loggedAt} as IUserAction
}

// Single typed decode of a buffered action's score payload. One owner so the
// Evaluate and calibration ownership checks don't hand-roll divergent payload casts.
export const scoreFromAction = (action: IUserAction): IRecordCompositeScoreInput | undefined =>
    (action.payload as { score?: IRecordCompositeScoreInput }).score

// Stable per-score client key, reused across that score's later edits. One owner so every scorer composes it
// identically.
export const makeClientId = (loggedAt: Date, userId?: string): string =>
    `${loggedAt.getTime()}-${userId}-${uuidV4()}`

// Sole owner of "no composite slot" → object-key mapping. Calibration scores carry a
// null compositeIndex; one constant keeps the wire-tree and the optimistic hash aligned.
export const NULL_COMPOSITE_INDEX_KEY = 'null'
export const compositeIndexKey = (compositeIndex?: string | number | null): string =>
    compositeIndex == null ? NULL_COMPOSITE_INDEX_KEY : String(compositeIndex)

// Buffer one score record/removal (stamps loggedAt + clientId, strips __typename, pushes the
// action). `buffered` settles when the write lands; a rejection means the score is stored
// nowhere, so every caller must handle it — a silent drop reads to the scorer as a save.
export const bufferScoreAction = <T extends IScoreActionRefs>(
    user: IUser | null | undefined,
    score: T,
    toAction: (op: Partial<T>) => IUserAction,
    { remove = false }: { remove?: boolean } = {},
): { loggedAt: Date; clientId?: string; buffered: Promise<void> } => {
    const loggedAt = new Date()
    const clientId = remove ? score.clientId : (score.clientId ?? makeClientId(loggedAt, user?._id))
    const op = (remove
        ? { ..._.omit(score, ['__typename']), loggedAt, remove: true }
        : { ..._.omit(score, ['__typename']), loggedAt, clientId }) as Partial<T>
    // A signed-out tap used to no-op silently; teardownSession clears the user var before it
    // awaits, so the scoring screen stays interactive through that window.
    const buffered = user?._id
        ? recordUserSessionActions(user, [toAction(op)]).then(() => undefined)
        : Promise.reject(new ScoreNotBufferedError('no-user'))
    return {
        loggedAt,
        clientId,
        buffered: buffered.catch((err) => {
            console.error('bufferScoreAction: failed to save the score on this device', err)
            throw err instanceof ScoreNotBufferedError ? err : new ScoreNotBufferedError('write-failed', err)
        }),
    }
}

// Walk every buffered action across the user's sessions, oldest-first, invoking fn.
// Sole owner of the replay iteration the score hashes are rebuilt from on load.
export const forEachBufferedAction = async (
    user: IUser,
    fn: (action: IUserAction) => void,
): Promise<void> => {
    const userSessions = await getAllCurrUsersSessions(user)
    userSessions?.forEach((session) => { session.actions.forEach(fn) })
}

// Single owner of the recordUserSession actions envelope (scores compress to the
// nested tree, player edits stay flat). See "recordUserSession — one `actions` envelope" in CLAUDE.md.
export const buildUserSessionActionsEnvelope = (actions: IUserAction[]) => {
    const playerEdits = actions.filter(a => a.actionType === 'PLAYER_EDIT')
    const scoreActions = actions.filter(a => a.actionType !== 'PLAYER_EDIT')

    const envelope: { scores?: Record<string, any>; playerEdits?: IUserAction[] } = {}
    if (scoreActions.length) envelope.scores = reduceUserActions(scoreActions)
    if (playerEdits.length) envelope.playerEdits = playerEdits
    return envelope
}

// Submit the current session in one recordUserSession call; pass actionsToSubmit (already stored) to flush
// a subset. Pure submit — callers remove acknowledged actions from the store.
export const submitUserSession = async (
    user: IUser,
    recordUserSessionMutation: (opts: { variables: Record<string, any> } & Record<string, any>) => Promise<any>,
    actionsToSubmit?: IUserAction[],
    mutateOptions?: Record<string, any>,
) => {
    const userSession = await getCurrUserSession(user)
    if (!userSession) return { userSession, recordUserSession: [] as IUserActionAck[] }

    const actions = actionsToSubmit ?? userSession.actions
    if (!actions.length) return { userSession, recordUserSession: [] as IUserActionAck[] }

    // Chunked like the offline hydrator. Sending the whole buffer in one request meant a device
    // that had been offline for a day could exceed the request size or the 120s timeout, and then
    // never drain: every retry re-sent the same oversized payload.
    const chunks = _.chunk(actions, SUBMIT_CHUNK_SIZE)
    const acks: IUserActionAck[] = []

    for (let i = 0; i < chunks.length; i += 1) {
        try {
            const results = await recordUserSessionMutation({
                variables: {
                    startedAt: userSession.startedAt,
                    actions: buildUserSessionActionsEnvelope(chunks[i]),
                    newUserId: user._id,
                    skipDuplicateActions: true,
                },
                // Only the last chunk carries the caller's options, so a refetch runs once per
                // submit rather than once per chunk.
                ...(i === chunks.length - 1 ? mutateOptions : undefined),
            })
            acks.push(...((results.data?.recordUserSession ?? []) as IUserActionAck[]))
        } catch (err) {
            // Keep the acks already earned: those actions are saved and must still be pruned.
            // The rest stay buffered, and the caller reports the failure from the missing acks.
            console.error('submitUserSession: chunk failed; keeping earlier acknowledgements', err)
            break
        }
    }

    return { userSession, recordUserSession: acks }
}

// A second copy of the same action: same type (the leaf array is per type), same instant as epoch ms, same raw.
const isSameFlatAction = (leaf: IFlatScoreAction, action: IUserAction, raw: string) =>
    toEpochMillis(leaf.loggedAt) === toEpochMillis(action.loggedAt) && leaf.raw === raw

// Collapses flat score actions into the compressed nested tree sent as actions.scores
// and consumed directly by the backend. See "recordUserSession — one `actions` envelope" in CLAUDE.md.
export const reduceUserActions = (actions: IUserAction[]) => {
    const flatSessionActions: Record<string, any> = {}
    for (const action of actions) {
      if (action.actionType == "score" || action.actionType == "removeScore") {
        // @ts-ignore
        const {drillId, skillId, round, assessmentId = "", ageGroupId = "", assessmentSessionId, playerAssessmentId, type, clientId = "", _id = "",  ...rest} = action.payload.score as IRecordScoreInput
        flatSessionActions[assessmentSessionId] = flatSessionActions[assessmentSessionId] ?? {}
        flatSessionActions[assessmentSessionId][drillId] = flatSessionActions[assessmentSessionId][drillId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId] = flatSessionActions[assessmentSessionId][drillId][skillId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round] = flatSessionActions[assessmentSessionId][drillId][skillId][round] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"]= flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId][_id] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId][_id] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId][_id][action.actionType] = (flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId][_id][action.actionType] ?? []) as IFlatScoreAction[]
  
        // Ignore a second copy of the same action.
        if (!flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId][_id][action.actionType].find((a: IFlatScoreAction) => isSameFlatAction(a, action, rest.raw)))
          flatSessionActions[assessmentSessionId][drillId][skillId][round]["score"][ageGroupId][assessmentId][playerAssessmentId][type][clientId][_id][action.actionType].push({..._.omit(rest, '__typename'), loggedAt: action.loggedAt})
      } else {
        // assessmentSessionId defaults to "" — calibration composites are session-less;
        // mirrors the score branch's empty-key defaults.

        // @ts-ignore
        const {drillId, skillId, round, assessmentSessionId = "", calibrationVideoId, compositeIndex: rawCompositeIndex, type, clientId = "", _id = "", ...rest} = action.payload.score as IRecordCompositeScoreInput
        // Calibration scores carry a null compositeIndex; the server strips it, reconciling
        // by calibrationVideoId. compositeIndexKey owns the null→key mapping.
        const compositeIndex = compositeIndexKey(rawCompositeIndex)
        flatSessionActions[assessmentSessionId] = flatSessionActions[assessmentSessionId] ?? {}
        flatSessionActions[assessmentSessionId][drillId] = flatSessionActions[assessmentSessionId][drillId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId] = flatSessionActions[assessmentSessionId][drillId][skillId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round] = flatSessionActions[assessmentSessionId][drillId][skillId][round] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId][_id] = flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId][_id] ?? {}
        flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId][_id][action.actionType] = (flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId][_id][action.actionType] ?? []) as IFlatScoreAction[]

        // ...rest carries raw + extra fields (calibration's additionalInfo.calibrationAssignmentId
        // included) onto the leaf for the backend to link. Skip a second copy of the same action.
        if (!flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId][_id][action.actionType].find((a: IFlatScoreAction) => isSameFlatAction(a, action, rest.raw)))
          flatSessionActions[assessmentSessionId][drillId][skillId][round]["composite"][calibrationVideoId][compositeIndex][type][clientId][_id][action.actionType].push({..._.omit(rest, '__typename'), loggedAt: action.loggedAt})
      }
    }

    return flatSessionActions
}
